<template>
    <div class="tableWithPerPageSelection">
        <div class="entete pdb-20px border-entete">
        <div class="level">
            <div class="level-left">
                <div class="columns is-gapless">
                    <div class="column">
                        <h1 class="title is-size-3">
                            {{ $t('detailsPlanningFaceUnitaire.title') }}
                        </h1>
                        <app-breadcrumb :breadcrumb="breadcrumb" />
                    </div>
                </div>
            </div>
        </div>
        </div>
        <div class="column is-narrow is-pulled-left mgt-10px">
            <button class="button" @click='changeDate(-1)'>
                <b-icon icon="chevron-left" size="is-small" type="is-default"></b-icon>
            </button>
        </div>
        <div class="column is-narrow is-pulled-left mgt-10px">
            <b-datepicker show-week-number icon="calendar-today"
              v-model="dateDebut"
              :editable="true"
              nearby-selectable-month-days
              @input="changementPeriode">
            </b-datepicker>
        </div>
        <div class="column is-pulled-left mgt-10px">
            <button class="button" @click='changeDate(1)'>
                <b-icon icon="chevron-right" size="is-small" type="is-default"></b-icon>
            </button>
        </div>
        <div class="column is-narrow is-pulled-left mgt-10px">
          <button class='button' :disabled="!canZoom"
                  @click='setMonthsInView("zoom")'>
            <b-icon icon="plus" size="is-small" type='is-default'></b-icon>
          </button>
          <button class='button' :disabled="!canDezoom"
                  @click="setMonthsInView('dezoom')">
            <b-icon icon="minus" size="is-small" type='is-default'></b-icon>
          </button>
        </div>
        <div class="column is-narrow is-pulled-left mgt-10px">
            <button class="button" @click='setShowFiltre'>
                <b-icon icon="filter-variant" size="is-small" type="is-default"></b-icon>
                <span class="is-size-7">
                    {{$t('detailsPlanningFaceUnitaire.actions.filtre')}}
                    <label class="has-text-weight-bold"
                       :class="nombreCriteres > 0 ? '' : 'is-invisible'">
                      ({{ nombreCriteres }})
                    </label>
                </span>
            </button>
            <span @click="reinitialiser()"
              class="info-circle-right is-pulled-right is-cursor-pointer">
              <b-icon icon="close-circle" type="is-danger"></b-icon>
            </span>
        </div>
        <div class="is-pulled-right mgt-20px">
          <exportExcelButton
            :perm="permCommandes.exporter"
            class="is-inline-block"
            in-progress/>
          <parametrage-table class="mgl-10px"
            :reference-table="referenceTable"
            @endLoadParamtre="startArrayRendering = true">
          </parametrage-table>
        </div>
        <v-server-table :ref="referenceTable"
          class="alignHeaderTab" v-if="startArrayRendering && mountedLoadingEnd"
          :options='options' :columns='columns'
          @loading='loading' @loaded='loaded'>
          <template slot='afterTable'>
            <table-items-in-view :perPage="resultPerPage"
                                 @input="$refs[referenceTable].setLimit($event)"/>
          </template>
          <div slot='reference' slot-scope='props'>
            <b-tooltip :label="props.row.reference" position="is-right">
              <router-link-blank class='ellipsis'
                                  :message='props.row.reference'
                                  :to='{ name: "c.bodac.catalogue-face",
                                params: { id: props.row.id } }'>
              </router-link-blank>
            </b-tooltip>
          </div>
          <div slot='produitFace.adresse' slot-scope='props'>
            <b-tooltip :label="props.row.produitFace.adresse | adresseComplete"
              position="is-right">
              <p class="ellipsis">
                {{ props.row.produitFace.adresse | adresseComplete }}
              </p>
            </b-tooltip>
          </div>
          <div slot='produitFace.adresse.ville' slot-scope='props'>
            <b-tooltip :label="props.row.produitFace.adresse.ville" position="is-right">
              <p class="ellipsis">
                {{ props.row.produitFace.adresse.ville }}
              </p>
            </b-tooltip>
          </div>
          <div slot="societe" slot-scope="props">
            <b-tooltip :label="getLibelleSociete(props.row.supportCommercial.societeId)"
                       position="is-right">
              <p class="ellipsis">
                {{ getLibelleSociete(props.row.supportCommercial.societeId) }}
              </p>
            </b-tooltip>
          </div>
        </v-server-table>
        <transition name="custom-classes-transition"
            enter-active-class="animate__animated animate__slideInRight"
            leave-active-class="animate__animated animate__slideOutRight" :duration="10">
            <div class="componentBrief" v-show="showFiltre === true">
                <filtre-planning-face-unitaire ref="filtrePlannifFaceUnitaire"
                    @toggleFiltre="setShowFiltre"
                    @recherche="recherche()"
                ></filtre-planning-face-unitaire>
            </div>
        </transition>
    </div>
</template>

<script>
import moment from 'moment';
import debounce from 'lodash/debounce';
import { createNamespacedHelpers } from 'vuex';
import AppBreadcrumb from '@/components/general/breadcrumb';
import { getSecteursByIds } from '@/services/commun/secteur/getSecteurs';
import { COMMERCIAL } from '@/configuration/parametrage/secteurConf';
import FiltrePlanningFaceUnitaire from '@/components/disponibilite/filtrePlanningFaceUnitaire';
import headerPlanningFaceUnitaireReseauLc from '@/services/planningFaceUnitaire/headerPlanningFaceUnitaireReseauLc';
import decorationPeriodeFaceUnitaireReseauLc from '@/components/planning/c_decorationPeriodeFaceUnitaireReseauLc';
import RouterLinkBlank from '@/components/general/routerLinkBlank';
import { COMMANDES } from '@/configuration/permissionsBodacConf';
import {
  VIEWPORT, MAX_MONTHS, MIN_MONTHS, MAX_MONTHS_AT_WIEWPORT,
} from '@/configuration/planning/planningConf';

import {
  NO_PER_PAGE_VALUE, RESULTS_PER_PAGE_PLANNING, SORT_ICON, TEXTS,
} from '@/configuration/clientTableConf';
import TableItemsInView from '@/components/general/TableItemsInView';
import messageErreur from '@/services/general/messageErreur';
import { columnsSplice } from '@/services/general/columns';
import { PARAMETRES_INTERNE_KEYS } from '@/store/modules/parametres-mutation-type';
import cloneDeep from 'lodash/cloneDeep';
import { SET_CRITERES_RECHERCHE_PLANNING } from '@/store/modules/produitFace-mutation-type';
import parametrageTable from '@/components/general/parametrageTable';
import exportExcelButton from '@/components/general/button/exportExcelButton';
import { REFERENCE_PLANNING_FACE } from '@/configuration/general/referenceTableConf';

const productsFaceUnitaire = createNamespacedHelpers('produitFace');
const societe = createNamespacedHelpers('societe');
const Parametres = createNamespacedHelpers('parametres');
const User = createNamespacedHelpers('user');

export default {
  name: 'CPlanningFaceUnitaire',
  filters: {
    adresseComplete(adresse) {
      return adresse.adresse1
        ? `${adresse.adresse1} ${adresse.adresse2 && ' - ' + adresse.adresse2} ${adresse.adresse3 && ' - ' + adresse.adresse3}`.trim()
        : '-';
    },
  },
  components: {
    AppBreadcrumb,
    FiltrePlanningFaceUnitaire,
    RouterLinkBlank,
    TableItemsInView,
    parametrageTable,
    exportExcelButton,
  },
  methods: {
    ...societe.mapActions({
      loadSocietes: 'loadSocietes',
    }),
    ...Parametres.mapActions({
      loadParametresInterne: 'loadParametresInterne',
    }),
    ...productsFaceUnitaire.mapMutations({
      setCritereRecherche: SET_CRITERES_RECHERCHE_PLANNING,
    }),
    getLibelleSociete(societeId) {
      const societe = this.societesInformations.find((societe) => {
        return societe.id === societeId;
      });
      return societe ? societe.libelle : null;
    },
    setMonthsInView(direction) {
      const sizes = {
        12: { zoom: 6, dezoom: null, viewport: VIEWPORT },
        6: { zoom: 3, dezoom: 12, viewport: null },
        3: { zoom: null, dezoom: 6, viewport: null },
      };
      const state = sizes[this.planningFaceUnitaire.months];
      const next = state[direction];
      if (next !== null) {
        const viewportCondition = sizes[next].viewport;
        if (!viewportCondition) {
          this.setMonths(next);
        } else if (window.innerWidth >= viewportCondition) {
          this.setMonths(next);
        }
      }
    },
    onResize: debounce(function onResize() {
      this.setPlanningFaceUnitaire();
    }, 300),
    changeDate(direction) {
      if (!this.dateDebut) return;
      this.dateDebut = (moment(this.dateDebut).add(1 * direction, 'months')).toDate();
      this.changementPeriode();
    },
    changementPeriode(value) {
      if (value) {
        this.dateDebut = moment(value).toDate();
      }
      this.critereRecherche.fromDate = moment(this.dateDebut).startOf('month').toDate();
      this.critereRecherche.toDate = (moment(this.dateDebut)).startOf('month').add(12, 'months').toDate();
      this.setStartDateInView(this.dateDebut);
      this.loading();
      this.setCritereRecherche(this.critereRecherche);
      this.getAffectations({
        fromDate: this.critereRecherche.fromDate,
        toDate: this.critereRecherche.toDate,
      }).then(() => { this.loaded(); }).catch(() => { this.loaded(); });
    },
    async setShowFiltre() {
      this.showFiltre = !this.showFiltre;
      if (this.showFiltre) await this.$refs.filtrePlannifFaceUnitaire.initializeFiltre();
    },
    recherche() {
      if (this.showFiltre) this.setShowFiltre();
      this.$refs[this.referenceTable].refresh();
    },
    ...productsFaceUnitaire.mapActions({
      setPlanningFaceUnitaire: 'setPlanningFaceUnitaire',
      setMonths: 'updateMonthsInPlanningView',
      setStartDateInView: 'updateStartDateInPlanningView',
      getProduits: 'fetchProduits',
      getAffectations: 'fetchAffectations',
    }),
    loading() {
      if (this.loader) {
        this.loader.close();
      }
      this.loader = this.$buefy.loading.open();
    },
    loaded() {
      this.loader.close();
    },
    async reinitialiser() {
      await this.$refs.filtrePlannifFaceUnitaire.initializeFiltre();
      this.$refs.filtrePlannifFaceUnitaire.viderCritere();
      this.$refs.filtrePlannifFaceUnitaire.appliquerCritere();
      this.showFiltre = false;
    },
  },
  data() {
    return {
      loader: null,
      dateDebut: new Date(),
      showFiltre: false,
      permCommandes: COMMANDES,
      startArrayRendering: false,
      mountedLoadingEnd: false,
      resultPerPage: RESULTS_PER_PAGE_PLANNING,
      referenceTable: REFERENCE_PLANNING_FACE,
    };
  },
  computed: {
    ...societe.mapGetters({
      societesInformations: 'societesInformations',
      societeActiveId: 'societeActiveId',
      agenceActive: 'agenceActive',
    }),
    ...Parametres.mapGetters({
      parametresInterne: 'parametresInterne',
    }),
    ...productsFaceUnitaire.mapGetters({
      produits: 'produits',
      totalProduits: 'totalProduits',
      planningFaceUnitaire: 'planningFaceUnitaire',
      critereRecherche: 'critereRecherchePlanning',
    }),
    ...User.mapGetters({
      user: 'user',
    }),
    moduleSecteurActive() {
      return this.parametresInterne(PARAMETRES_INTERNE_KEYS.moduleSecteur.key);
    },
    filtrerProduitParSociete() {
      return this.parametresInterne(PARAMETRES_INTERNE_KEYS.filtrerProduitParSociete.key);
    },
    filtrerProduitParAgence() {
      return this.parametresInterne(PARAMETRES_INTERNE_KEYS.filtrerProduitParAgence.key);
    },
    options() {
      return {
        sortIcon: SORT_ICON,
        headings: {
          reference: this.$i18n.t('detailsPlanningFaceUnitaire.tab.reference'),
          'produitFace.adresse': this.$i18n.t('detailsPlanningFaceUnitaire.tab.adresse'),
          'produitFace.adresse.ville': this.$i18n.t('detailsPlanningFaceUnitaire.tab.ville'),
          societe: this.$i18n.t('detailsPlanningFaceUnitaire.tab.societe'),
          planning: (h) => {
            return headerPlanningFaceUnitaireReseauLc(h,
              this.planningFaceUnitaire.fromDate,
              moment(this.planningFaceUnitaire.fromDate)
                .add(this.planningFaceUnitaire.months, 'months').toDate(),
              'month');
          },
        },
        columnsClasses: {
          ...this.columns.reduce((acc, val) => {
            acc[val] = 'sticky';
            return acc;
          }, {}),
          reference: 'planning-face-unitaire code sticky',
          'produitFace.adresse': 'planning-face-unitaire address sticky',
          'produitFace.adresse.ville': 'planning-face-unitaire city sticky',
          planning: 'planning-face-unitaire planning sticky',
          societe: 'planning-face-unitaire societe sticky',
        },
        filterable: false,
        perPageValues: NO_PER_PAGE_VALUE,
        perPage: this.resultPerPage,
        texts: TEXTS,
        templates: {
          planning: decorationPeriodeFaceUnitaireReseauLc,
        },
        sortable: ['reference', 'produitFace.adresse', 'produitFace.adresse.ville'],
        requestFunction: ((data) => {
          if (this.filtrerProduitParAgence && this.critereRecherche.agencesId.length === 0) {
            this.critereRecherche.agencesId = this.agenceActive.map((agence) => agence.agenceId);
          }
          if (!data.query) data.query = cloneDeep(this.critereRecherche);
          if (this.filtrerProduitParSociete) {
            data.query.societeId = this.societeActiveId;
          }
          return this.getProduits(data);
        }),
        responseAdapter: (() => {
          return {
            data: this.produits,
            count: this.totalProduits,
          };
        }),
      };
    },
    nombreCriteres() {
      let nombreCritere = this.critereRecherche.reference ? 1 : 0;
      nombreCritere += this.critereRecherche.villes.length;
      nombreCritere += this.critereRecherche.formats.length;
      nombreCritere += this.critereRecherche.materiels.length;
      nombreCritere += this.critereRecherche.secteurIds.length;
      nombreCritere += this.critereRecherche.societeId ? 1 : 0;
      nombreCritere += this.critereRecherche.avecAffectationReseau ? 1 : 0;
      nombreCritere += this.critereRecherche.eclaire !== null ? 1 : 0;
      return nombreCritere <= 9 ? nombreCritere : '+9';
    },
    columns() {
      const columns = ['reference', 'produitFace.adresse', 'produitFace.adresse.ville', 'societe', 'planning'];
      if (this.societesInformations.length === 1 || this.filtrerProduitParSociete
        || this.filtrerProduitParAgence) {
        columnsSplice(columns, 'societe');
      }
      return columns;
    },
    breadcrumb() {
      return [
        { title: this.$i18n.t('detailsPlanningFaceUnitaire.racine'), route: this.$PAGE_DEFAULT, isActive: false },
        { title: this.$i18n.t('detailsPlanningFaceUnitaire.title'), route: { name: '' }, isActive: true },
      ];
    },
    canZoom() {
      return this.planningFaceUnitaire.months > MIN_MONTHS;
    },
    canDezoom() {
      return (this.planningFaceUnitaire.months < MAX_MONTHS && window.innerWidth >= VIEWPORT)
        || (this.planningFaceUnitaire.months < MAX_MONTHS_AT_WIEWPORT
          && window.innerWidth < VIEWPORT);
    },
  },
  async mounted() {
    const loadingComponent = this.$buefy.loading.open();
    try {
      await this.loadSocietes({});
      this.setPlanningFaceUnitaire({
        fromDate: moment(this.critereRecherche.fromDate).startOf('month').toDate(),
        toDate: moment(this.critereRecherche.toDate).endOf('month').toDate(),
      });
      await this.loadParametresInterne([
        PARAMETRES_INTERNE_KEYS.filtrerProduitParSociete.key,
        PARAMETRES_INTERNE_KEYS.filtrerProduitParAgence.key,
        PARAMETRES_INTERNE_KEYS.moduleSecteur.key,
      ]);
      if (this.filtrerProduitParAgence) {
        this.critereRecherche.agencesId = this.agenceActive.map((agence) => agence.agenceId);
      }
      if (this.moduleSecteurActive) {
        if (this.user.secteursId.length === 0
          && this.critereRecherche.secteurIds.length > 0) {
          this.critereRecherche.secteurIds = (await getSecteursByIds(
            this.critereRecherche.secteurIds,
            COMMERCIAL)).map(secteur => secteur.id);
        } else this.critereRecherche.secteurIds = this.user.secteursId ?? [];
      }
      this.mountedLoadingEnd = true;
      this.setInformationPage(this.$t('pageGescom.planning.face'));
      window.addEventListener('resize', this.onResize, { passive: true });
      loadingComponent.close();
    } catch (e) {
      messageErreur(this.$i18n);
      loadingComponent.close();
      throw e;
    }
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.onResize);
  },
};
</script>
